import StackKit from "@/shared/components/kit/stack";
import TabPanel from "@mui/lab/TabPanel";
import Tab from "@mui/material/Tab";
import React, { Dispatch, SetStateAction, useState } from "react";
import Box from "@mui/material/Box";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { TextField } from "@mui/material";
import AdapterMomentJalali from "@/shared/components/common/datePeacker";
import moment from "jalali-moment";

interface PossibilityOfCashingAssetsModalProps {
  setDescription: Dispatch<SetStateAction<string>>;
  setBalanceAmount: Dispatch<SetStateAction<string>>;
  balanceAmount: string;
  description: string;
  setValueUserCharge: Dispatch<SetStateAction<string>>;
  valueUserCharge: string;
}
/**
 * PossibilityOfCashingAssetsModal
 *
 * یک کامپوننت مدال برای مدیریت اعتبار نقدی داخلی کاربران انتخاب شده در سازمان.
 * این مدال دارای دو تب "افزایش موجودی" و "کاهش موجودی" است که هرکدام شامل فیلدهای
 * مبلغ اعتبار، مدت بازپرداخت (برای افزایش موجودی) و توضیحات می‌باشد.
 * کامپوننت از `TabContext` و `TabPanel` برای مدیریت تب‌ها و `AdapterMomentJalali` برای انتخاب تاریخ
 * استفاده می‌کند.
 *
 * @param setDescription - تابع برای بروزرسانی متن توضیحات.
 * @param setBalanceAmount - تابع برای بروزرسانی مقدار اعتبار نقدی.
 * @param balanceAmount - مقدار فعلی اعتبار نقدی.
 * @param description - متن توضیحات فعلی.
 * @param valueUserCharge - تب فعلی انتخاب شده ("1" برای افزایش، "2" برای کاهش).
 * @param setValueUserCharge - تابع برای تغییر تب انتخاب شده.
 *
 * @returns JSX.Element
 */

const PossibilityOfCashingAssetsModal = ({ setDescription, setBalanceAmount, balanceAmount, description, valueUserCharge, setValueUserCharge, }: PossibilityOfCashingAssetsModalProps) => {

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValueUserCharge(newValue);
  };

  const [valueDate, setValuDate] = useState(null);

  return (
    <StackKit gap={2}>
      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={valueUserCharge}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={handleChange} aria-label="lab API tabs example" variant="scrollable" scrollButtons="auto">
              <Tab label="افزایش موجودی" value="1" />
              <Tab label="کاهش موجودی" value="2" />
            </TabList>
          </Box>

          {/* TabPanel: افزایش موجودی */}
          <TabPanel value="1">
            <StackKit gap={2}>
              <TypoGraphyKit variant="body1">
                پذیرنده گرامی ؛ در این بخش شما میتوانید اعتبار نقدی داخلی همه کاربران انتخاب شده را مدیریت کنید.
              </TypoGraphyKit>
              <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                <TypoGraphyKit variant="body1" sx={{ minWidth: { xs: "100%", sm: "40%" } }}>
                  مبلغ اعتبار نقدی داخلی (ریال):
                </TypoGraphyKit>
                <TextField sx={{ width: { xs: "100%", sm: "55%" } }} value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)} />
              </StackKit>
              <StackKit direction={"column"} gap={2} sx={{ marginLeft: '0px !important'}}>
                <TypoGraphyKit textAlign={'right'}>مدت بازپرداخت:</TypoGraphyKit>
                <StackKit direction="column" gap={2} sx={{ marginLeft: '0px !important'}}>
                  <StackKit direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent={'flex-end'} textAlign={'right'} gap={1} sx={{ width: '100%' }}>
                    <TypoGraphyKit textAlign={'right'}>از</TypoGraphyKit>
                    <AdapterMomentJalali widthProps={'100%'} sx={{ width: "100%", height: "50px" }} valueDate={valueDate ? moment(valueDate, "jYYYY/jMM/jDD") : null} handleChange={(newValue) => setValuDate(newValue?.format("jYYYY/jMM/jDD") || "")} />
                  </StackKit>
                  <StackKit direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent={'flex-end'} textAlign={'right'} gap={1} sx={{ width: '100%' }}>
                    <TypoGraphyKit >تا</TypoGraphyKit>
                    <AdapterMomentJalali widthProps={'100%'} sx={{ width: "100%", height: "50px" }} valueDate={valueDate ? moment(valueDate, "jYYYY/jMM/jDD") : null} handleChange={(newValue) => setValuDate(newValue?.format("jYYYY/jMM/jDD") || "")} />
                  </StackKit>
                </StackKit>
              </StackKit>
              <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                <TypoGraphyKit variant="body1" textAlign={'right'}>توضیحات :</TypoGraphyKit>
                <TextField sx={{ width: { xs: "100%", sm: "60%" } }} value={description} onChange={(e) => setDescription(e.target.value)} />
              </StackKit>
            </StackKit>
          </TabPanel>

          {/* TabPanel: کاهش موجودی */}
          <TabPanel value="2">
            <StackKit gap={2}>
              <TypoGraphyKit variant="body1">
                پذیرنده گرامی ؛ در این بخش شما میتوانید اعتبار نقدی داخلی همه کاربران انتخاب شده را مدیریت کنید.
              </TypoGraphyKit>
              <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                <TypoGraphyKit variant="body1">مبلغ اعتبار نقدی داخلی (ریال):</TypoGraphyKit>
                <TextField sx={{ width: { xs: "100%", sm: "50%" } }} value={balanceAmount} onChange={(e) => setBalanceAmount(e.target.value)} inputProps={{ maxLength: 10 }} />
              </StackKit>
              <StackKit direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2}>
                <TypoGraphyKit variant="body1">توضیحات :</TypoGraphyKit>
                <TextField sx={{ width: { xs: "100%", sm: "50%" } }} value={description} onChange={(e) => setDescription(e.target.value)} inputProps={{ maxLength: 10 }} />
              </StackKit>
            </StackKit>
          </TabPanel>
        </TabContext>
      </Box>
    </StackKit>
  );
};

export default PossibilityOfCashingAssetsModal;
